import { AxiosResponse } from 'axios';
import { useMutation } from 'react-query';

import { api } from '../../../http';
import { DefaultRes } from '../../../types';
import { queryClient } from '../../../services/react-query/react-query.client';
import { useGenericQuery } from '../../../services/react-query/generic.query';

/**
 * Data access for the admin-approved account deletion page (ticket 868kq0nb9).
 * Requests are filed by the mobile app via node-api when the
 * app.accountDeletion.requireAdminApproval setting is on; this page lists and
 * decides them through pom-2-api's /v1/account-deletion-requests endpoints
 * (ADMIN / SUPER_ADMIN only).
 */

export interface DeletionRequestRow {
    id: string;
    userId: string;
    requestedAt: string;
    name: string;
    phoneNumber: string | null;
    employeeId: string | null;
}

export interface DecisionOutcome {
    success: boolean;
    status: 'approved' | 'denied';
}

const PENDING_LIST_QUERY = 'account-deletion-requests-pending';
const PENDING_LIST_URL = 'account-deletion-requests';

const invalidatePendingList = () => queryClient.invalidateQueries([PENDING_LIST_QUERY]);

export const usePendingDeletionRequests = () =>
    useGenericQuery<DeletionRequestRow[]>(PENDING_LIST_QUERY, PENDING_LIST_URL);

export const useApproveDeletionRequest = () =>
    useMutation(
        async (requestId: string) => {
            const { data } = await api.post<DefaultRes<DecisionOutcome>, AxiosResponse<DefaultRes<DecisionOutcome>>>(
                `/account-deletion-requests/${requestId}/approve`,
            );
            return data.result;
        },
        // Refresh on error too: a 404/409 means another admin already decided
        // this request, so the stale row should disappear either way.
        { onSuccess: invalidatePendingList, onError: invalidatePendingList },
    );

export const useDenyDeletionRequest = () =>
    useMutation(
        async (requestId: string) => {
            const { data } = await api.post<DefaultRes<DecisionOutcome>, AxiosResponse<DefaultRes<DecisionOutcome>>>(
                `/account-deletion-requests/${requestId}/deny`,
            );
            return data.result;
        },
        { onSuccess: invalidatePendingList, onError: invalidatePendingList },
    );
